import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Mail, Phone, User, MessageSquare } from 'lucide-react';
import { useLanguage } from '../LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactModal = ({ isOpen, onClose, productName = '' }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Send to backend API
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        product_name: productName
      });

      console.log('Contact inquiry saved:', response.data);
      
      setIsSubmitting(false);
      setIsSuccess(true);

      // Reset form and close after 2 seconds
      setTimeout(() => {
        setFormData({ name: '', email: '', phone: '', message: '' });
        setIsSuccess(false);
        setError('');
        onClose();
      }, 2000);
      
    } catch (err) {
      console.error('Error submitting contact form:', err);
      setIsSubmitting(false);
      setError('Failed to send message. Please try again or contact us directly at contact@lvluxury.ch');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-[500px]"
        style={{ backgroundColor: 'var(--cream-white)' }}
      >
        <DialogHeader>
          <DialogTitle 
            className="text-2xl font-light"
            style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}
          >
            {t.contactModal.title}
          </DialogTitle>
          <DialogDescription style={{ color: 'var(--warm-gray)' }}>
            {productName ? (
              <span>{t.contactModal.inquiringAbout} <strong style={{ color: 'var(--lv-brown)' }}>{productName}</strong></span>
            ) : (
              <span>{t.contactModal.getInTouch}</span>
            )}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div 
            className="p-3 rounded-lg mb-4"
            style={{ backgroundColor: '#FEE2E2', border: '1px solid #DC2626' }}
          >
            <p style={{ color: '#DC2626', fontSize: '14px' }}>
              {error}
            </p>
          </div>
        )}

        {isSuccess ? (
          <div className="py-8 text-center">
            <div 
              className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'var(--warm-sage)' }}
            >
              <Mail className="w-8 h-8" style={{ color: 'var(--cream-white)' }} />
            </div>
            <h3 
              className="text-xl font-medium mb-2"
              style={{ color: 'var(--charcoal)' }}
            >
              {t.contactModal.successTitle}
            </h3>
            <p style={{ color: 'var(--warm-gray)' }}>
              {t.contactModal.successMessage}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Name Field */}
            <div className="space-y-2">
              <Label 
                htmlFor="name"
                style={{ color: 'var(--charcoal)' }}
              >
                {t.contactModal.fullName}
              </Label>
              <div className="relative">
                <User 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                  style={{ color: 'var(--warm-gray)' }}
                />
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t.contactModal.fullNamePlaceholder}
                  className="pl-10"
                  style={{ 
                    backgroundColor: 'white',
                    border: '1px solid var(--soft-taupe)',
                    borderRadius: '8px'
                  }}
                />
              </div>
            </div>

            {/* Email Field */}
            <div className="space-y-2">
              <Label 
                htmlFor="email"
                style={{ color: 'var(--charcoal)' }}
              >
                {t.contactModal.email}
              </Label>
              <div className="relative">
                <Mail 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                  style={{ color: 'var(--warm-gray)' }}
                />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t.contactModal.emailPlaceholder}
                  className="pl-10"
                  style={{ 
                    backgroundColor: 'white',
                    border: '1px solid var(--soft-taupe)',
                    borderRadius: '8px'
                  }}
                />
              </div>
            </div>

            {/* Phone Field */}
            <div className="space-y-2">
              <Label 
                htmlFor="phone"
                style={{ color: 'var(--charcoal)' }}
              >
                {t.contactModal.phone}
              </Label>
              <div className="relative">
                <Phone 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                  style={{ color: 'var(--warm-gray)' }}
                />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t.contactModal.phonePlaceholder}
                  className="pl-10"
                  style={{ 
                    backgroundColor: 'white',
                    border: '1px solid var(--soft-taupe)',
                    borderRadius: '8px'
                  }}
                />
              </div>
            </div>

            {/* Message Field */}
            <div className="space-y-2">
              <Label 
                htmlFor="message"
                style={{ color: 'var(--charcoal)' }}
              >
                {t.contactModal.message}
              </Label>
              <div className="relative">
                <MessageSquare 
                  className="absolute left-3 top-3 w-5 h-5"
                  style={{ color: 'var(--warm-gray)' }}
                />
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t.contactModal.messagePlaceholder}
                  rows={4}
                  className="pl-10 pt-3"
                  style={{ 
                    backgroundColor: 'white',
                    border: '1px solid var(--soft-taupe)',
                    borderRadius: '8px'
                  }}
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
                style={{ 
                  borderColor: 'var(--warm-gray)',
                  color: 'var(--charcoal)'
                }}
              >
                {t.contactModal.cancel}
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 uppercase tracking-wider"
                style={{ 
                  backgroundColor: 'var(--warm-sage)',
                  color: 'var(--cream-white)',
                  letterSpacing: '1px'
                }}
              >
                {isSubmitting ? t.contactModal.sending : t.contactModal.sendMessage}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;